import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import PreviewCode from './PreviewCode';
import { getAuthErrorMessage } from '../utils/errorMessages';
import { reportHostIssue } from '../utils/hostIssues/hostIssueReporter';
import { useInvitationAcceptance } from '../hooks/useInvitationAcceptance';
import { InvitationBanner, InvitationError } from './shared/InvitationNotice';
import { Button, IconButton, Input } from './ui';
import { safeReturnTo } from '../utils/returnTo';
import '../styles/Login.css';

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="#2A2A24" style={{ width: 18, height: 18 }}>
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.48-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09l-.05-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreviewCode, setShowPreviewCode] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const navigate = useNavigate();
  // A page this sign-in came from (a team's join page): it decides where to
  // land by itself, as an invitation does — no preview code, no waitlist.
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const {
    invitationToken,
    invitationError,
    accepting: acceptingInvitation,
    acceptInvitation,
  } = useInvitationAcceptance();

  // An invitation link is the only way back for someone whose acceptance
  // failed the first time: they now have an account, so /signup throws
  // `auth/email-already-in-use` before acceptance is ever reached. Expiry and
  // every other check stay server-side — this just calls the same function.
  const handleRetryInvitation = async () => {
    setLoading(true);
    const retry = await acceptInvitation();
    setLoading(false);
    if (retry.ok) navigate('/');
  };

  // Shown on screen, and reported when inside the iOS app: a device build
  // keeps no console, and signing in there means the handover already failed.
  // A no-op in a browser.
  const showSignInError = (method, err) => {
    setError(getAuthErrorMessage(err));
    reportHostIssue('ios-sign-in-failed', {
      error: err,
      context: { method, code: err?.code },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.signIn(email, password);

      if (invitationToken) {
        const accepted = await acceptInvitation();
        // Stay put and say why; never send an invitee to the waitlist or
        // the preview-code screen over a failed acceptance.
        if (!accepted.ok) return;
      }

      navigate(returnTo ?? '/');
    } catch (error) {
      showSignInError('password', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await authService.signInWithGoogle();

      // An invitation decides the destination by itself: accepting it grants
      // access, so the `hasPreviewAccess` read a moment ago is already stale.
      if (invitationToken) {
        const accepted = await acceptInvitation();
        if (accepted.ok) navigate('/');
        else setLoading(false);
        return;
      }

      if (returnTo) {
        if (result.isNewUser) await authService.createWaitlistUser(result.user);
        navigate(returnTo);
        return;
      }

      if (result.isNewUser) {
        setNewUser(result.user);
        setShowPreviewCode(true);
        setLoading(false);
      } else if (result.hasPreviewAccess) {
        navigate('/');
      } else {
        navigate('/waitlist');
      }
    } catch (error) {
      showSignInError('google', error);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await authService.signInWithApple();

      // An invitation decides the destination by itself: accepting it grants
      // access, so the `hasPreviewAccess` read a moment ago is already stale.
      if (invitationToken) {
        const accepted = await acceptInvitation();
        if (accepted.ok) navigate('/');
        else setLoading(false);
        return;
      }

      if (returnTo) {
        if (result.isNewUser) await authService.createWaitlistUser(result.user);
        navigate(returnTo);
        return;
      }

      if (result.isNewUser) {
        setNewUser(result.user);
        setShowPreviewCode(true);
        setLoading(false);
      } else if (result.hasPreviewAccess) {
        navigate('/');
      } else {
        navigate('/waitlist');
      }
    } catch (error) {
      showSignInError('apple', error);
      setLoading(false);
    }
  };

  const handlePreviewCodeComplete = (hasAccess) => {
    if (hasAccess) {
      navigate('/');
    } else {
      navigate('/waitlist');
    }
  };

  if (showPreviewCode && newUser) {
    return (
      <PreviewCode user={newUser} onComplete={handlePreviewCodeComplete} />
    );
  }

  const passwordToggle = (
    <IconButton
      aria-label={showPassword ? 'Hide password' : 'Toggle password visibility'}
      tone="dark"
      variant="ghost"
      size="sm"
      onClick={() => setShowPassword(!showPassword)}
      tabIndex={-1}
    >
      <EyeIcon off={!showPassword} />
    </IconButton>
  );

  return (
    <div className="login-container">
      <Link to="/login" className="back-to-welcome">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </Link>
      <div className="login-box">
        <div className="logo-container">
          <img src="/FullLogo.png" alt="Housfully" className="logo" />
        </div>
        <h1 className="preview-title">Preview Edition</h1>
        <p className="subtitle">
          {invitationToken
            ? 'Log in to accept the project invitation'
            : 'Log in to continue to your workspace'}
        </p>
        {invitationToken && (
          <InvitationBanner>
            You've been invited to view a project. Log in to accept the
            invitation.
          </InvitationBanner>
        )}
        <InvitationError
          message={invitationError}
          onRetry={handleRetryInvitation}
          disabled={loading || acceptingInvitation}
        />
        {error && <div className="error-message">{error}</div>}
        <form
          onSubmit={handleSubmit}
          className="login-form"
          data-testid="login-form"
        >
          <Input
            tone="dark"
            type="email"
            id="email"
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            disabled={loading}
            leadingIcon={<EmailIcon />}
          />
          <Input
            tone="dark"
            type={showPassword ? 'text' : 'password'}
            id="password"
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            disabled={loading}
            leadingIcon={<LockIcon />}
            trailingIcon={passwordToggle}
            trailingInteractive
          />
          <div className="forgot-password-link">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
          <Button
            type="submit"
            tone="dark"
            variant="primary"
            fullWidth
            loading={loading}
            data-testid="login-submit"
          >
            {loading ? 'Logging in...' : 'Log In'}
          </Button>
        </form>
        <div className="login-oauth">
          <Button
            type="button"
            tone="dark"
            variant="soft"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={loading}
            leadingIcon={<GoogleIcon />}
          >
            Continue with Google
          </Button>
          <Button
            type="button"
            tone="dark"
            variant="soft"
            fullWidth
            onClick={handleAppleSignIn}
            disabled={loading}
            leadingIcon={<AppleIcon />}
          >
            Continue with Apple
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Login;
